import api, { kioskHttp } from './api'

export type TeamScheme = 'groups' | 'personal'
export type TeamRole = 'owner' | 'moderator' | 'member'

export interface Team {
  id: number
  name: string
  slug: string
  scheme: TeamScheme
  personal_share_bp: number | null
  personal_pool_tip_group: { id: number; name: string } | null
  personal_pool_pool_group: { id: number; name: string } | null
  scheme_locked_at: string | null
  is_scheme_locked: boolean
  is_active: boolean
  /** Personal pool: false when tipped-staff or shared-pool bucket has no members. */
  is_team_active?: boolean
  owner_id: number
  /** Present on GET /api/teams/:id/ — caller's membership role. */
  viewer_role?: TeamRole | null
  tips_url?: string
  qr_url?: string
  logo_url?: string | null
  /** True if any tip was distributed through this team (blocks permanent DELETE). */
  has_tip_history?: boolean
  tip_preset_1: number
  tip_preset_2: number
  tip_preset_3: number
  tip_preset_4: number
  instagram_url?: string | null
  facebook_url?: string | null
  google_place_id?: string | null
  tripadvisor_url?: string | null
  track_google_review_clicks?: boolean
  track_tripadvisor_review_clicks?: boolean
  created_at: string
  updated_at: string
}

export interface TeamProfilePayload {
  name?: string
  tip_preset_1?: number
  tip_preset_2?: number
  tip_preset_3?: number
  tip_preset_4?: number
  instagram_url?: string | null
  facebook_url?: string | null
  google_place_id?: string | null
  tripadvisor_url?: string | null
  track_google_review_clicks?: boolean
  track_tripadvisor_review_clicks?: boolean
  clear_logo?: boolean
}

export interface TeamActivation {
  public_slug: string
  kiosk_url: string
  qr_file_url: string | null
  pin_changed_at: string | null
  locked_until: string | null
  failed_attempts: number
}

export interface KioskSessionResponse {
  token: string
  expires_at: string
  team: { id: number; name: string }
}

/** Present when the team uses the groups tip scheme and the user is in a pool group. */
export interface KioskMemberGroup {
  id: number
  name: string
  position: number
  /** Basis points 0…10_000; display as `percentage_bp / 100` percent. */
  percentage_bp: number
  is_default: boolean
}

export interface KioskMember {
  id: number
  display_name: string
  user_email: string
  is_active: boolean
  role: TeamRole
  group: KioskMemberGroup | null
}

export type TeamQrSides = { top: string; right: string; bottom: string; left: string }

export interface TeamListItem extends Team {
  role: TeamRole
  /** Sum of this user's tip shares from this team (cents). */
  my_all_time_cents?: number
  my_shares_count?: number
}

/** GET /api/teams/:id/stats/team/ — moderator+ aggregate tip totals. */
export interface TeamAggregatePeriodStats {
  total_cents: number
  tips_count: number
  review_clicks?: TeamReviewClickStats
}

export interface TeamReviewClickStats {
  google: number
  tripadvisor: number
}

export interface TeamAggregateStatsResponse {
  week: TeamAggregatePeriodStats
  month: TeamAggregatePeriodStats
  all_time: TeamAggregatePeriodStats
  review_clicks?: {
    week: TeamReviewClickStats
    month: TeamReviewClickStats
    all_time: TeamReviewClickStats
  }
}

/** Same shape as a period bucket when `?start=` / `?end=` are sent (TipDistribution window). */
export type TeamTipIntervalStats = TeamAggregatePeriodStats

export interface TeamCreateResponse extends Team {
  initial_kiosk_pin?: string | null
}

export interface TeamMembership {
  id: number
  team_id: number
  user_id: number
  user_email: string
  user_first_name: string
  user_last_name: string
  user_avatar?: string | null
  role: TeamRole
  is_active: boolean
  manual_override: boolean | null
  manual_override_at: string | null
  joined_at: string
  left_at: string | null
}

export type InviteStatus = 'pending' | 'accepted' | 'declined' | 'cancelled' | 'expired'

export interface TeamInvite {
  id: number
  team_id: number
  invited_user_id: number | null
  /** Set when the person does not have an account yet (pending pre-registration). */
  invited_email?: string | null
  invited_user_email: string
  invited_user_name: string
  invited_by_id: number | null
  invited_by_email: string | null
  role: TeamRole
  status: InviteStatus
  created_at: string
  expires_at: string
  responded_at: string | null
}

export interface MyInvite {
  id: number
  team_id: number
  team_name: string
  team_slug: string
  invited_by_id: number | null
  invited_by_name: string | null
  role: TeamRole
  status: InviteStatus
  created_at: string
  expires_at: string
  responded_at: string | null
}

export interface StaffInboxItem {
  id: number
  team_id: number
  team_name: string
  kind: string
  title: string
  body: string
  created_at: string
  read_at: string | null
}

export interface StaffInboxResponse {
  items: StaffInboxItem[]
  unread_count: number
}

export interface TeamGroupMember {
  membership_id: number
  user_id: number
  user_email: string
  user_first_name: string
  user_last_name: string
  user_avatar?: string | null
  role: TeamRole
  is_active: boolean
}

export interface TeamGroup {
  id: number
  team_id: number
  name: string
  percentage_bp: number
  position: number
  is_default: boolean
  created_at: string
  members_count: number
  members?: TeamGroupMember[]
}

export interface GroupPercentageItem {
  id: number
  bp: number
}

export interface InviteLookupResult {
  found: boolean
  /** If true, you may create an invite that activates when that email registers. */
  can_invite_unregistered?: boolean
  user_id?: number
  email?: string
  first_name?: string
  last_name?: string
  is_member?: boolean
  has_pending_invite?: boolean
}

export interface CreateTeamPayload {
  name: string
  scheme: TeamScheme
  personal_share_bp?: number | null
  /** Display name for staff who keep the tipped personal slice (personal scheme). */
  personal_tip_group_name?: string | null
  /** Display name for the bucket that splits everything after the personal slice. */
  personal_pool_group_name?: string | null
}

export const teamsApi = {
  list(): Promise<TeamListItem[]> {
    return api.get('/api/teams/').then((r) => r.data)
  },

  create(payload: CreateTeamPayload): Promise<TeamCreateResponse> {
    return api.post('/api/teams/', payload).then((r) => r.data)
  },

  detail(teamId: number | string): Promise<Team> {
    return api.get(`/api/teams/${teamId}/`).then((r) => r.data)
  },

  update(teamId: number | string, payload: TeamProfilePayload | FormData): Promise<Team> {
    const isForm = typeof FormData !== 'undefined' && payload instanceof FormData
    return api
      .patch(`/api/teams/${teamId}/`, isForm ? payload : { ...payload }, isForm ? {} : undefined)
      .then((r) => r.data)
  },

  updatePersonalShare(teamId: number | string, personal_share_bp: number): Promise<Team> {
    return api.put(`/api/teams/${teamId}/personal-share/`, { personal_share_bp }).then((r) => r.data)
  },

  /** Permanently delete team (solo owner). Tip payout snapshots are kept with `team` unset. */
  remove(teamId: number | string): Promise<void> {
    return api.delete(`/api/teams/${teamId}/`).then((r) => r.data)
  },

  /** Soft-delete / deactivate (`is_active=false`). Prefer `remove` from the UI. */
  deactivate(teamId: number | string): Promise<void> {
    return api.post(`/api/teams/${teamId}/deactivate/`).then((r) => r.data)
  },

  /** Member/moderator leaves voluntarily (owner must transfer first). */
  leave(teamId: number | string): Promise<void> {
    return api.post(`/api/teams/${teamId}/leave/`).then(() => undefined)
  },

  transferOwnership(teamId: number | string, newOwnerUserId: number): Promise<Team> {
    return api
      .post(`/api/teams/${teamId}/transfer-ownership/`, { new_owner_user_id: newOwnerUserId })
      .then((r) => r.data)
  },

  members(teamId: number | string): Promise<TeamMembership[]> {
    return api.get(`/api/teams/${teamId}/members/`).then((r) => r.data)
  },

  aggregateStats(
    teamId: number | string,
    params?: { start?: string; end?: string },
  ): Promise<TeamAggregateStatsResponse | TeamTipIntervalStats> {
    const q = params && (params.start || params.end) ? { params } : {}
    return api.get(`/api/teams/${teamId}/stats/team/`, q).then((r) => r.data)
  },

  listGroups(teamId: number | string): Promise<TeamGroup[]> {
    return api.get(`/api/teams/${teamId}/groups/`).then((r) => r.data)
  },

  createGroup(teamId: number | string, payload: { name: string }): Promise<TeamGroup> {
    return api.post(`/api/teams/${teamId}/groups/`, payload).then((r) => r.data)
  },

  renameGroup(teamId: number | string, groupId: number, name: string): Promise<TeamGroup> {
    return api.patch(`/api/teams/${teamId}/groups/${groupId}/`, { name }).then((r) => r.data)
  },

  updateGroupPercentages(teamId: number | string, items: GroupPercentageItem[]): Promise<TeamGroup[]> {
    return api.put(`/api/teams/${teamId}/groups/percentages/`, { items }).then((r) => r.data)
  },

  deleteGroup(teamId: number | string, groupId: number, reassignToId: number): Promise<void> {
    return api
      .delete(`/api/teams/${teamId}/groups/${groupId}/`, {
        data: { reassign_to: reassignToId },
      })
      .then((r) => r.data)
  },

  removeMember(teamId: number | string, membershipId: number): Promise<void> {
    return api.delete(`/api/teams/${teamId}/members/${membershipId}/`).then((r) => r.data)
  },

  moveMemberToGroup(
    teamId: number | string,
    membershipId: number,
    targetGroupId: number,
  ): Promise<{ team_id: number; group_id: number; user_id: number }> {
    return api
      .post(`/api/teams/${teamId}/members/${membershipId}/move-group/`, {
        target_group_id: targetGroupId,
      })
      .then((r) => r.data)
  },

  listInvites(teamId: number | string): Promise<TeamInvite[]> {
    return api.get(`/api/teams/${teamId}/invites/`).then((r) => r.data)
  },

  lookupInviteEmail(teamId: number | string, email: string): Promise<InviteLookupResult> {
    return api.get(`/api/teams/${teamId}/invites/lookup/`, { params: { email } }).then((r) => r.data)
  },

  createInvite(teamId: number | string, payload: { email: string; role?: TeamRole }): Promise<TeamInvite> {
    return api.post(`/api/teams/${teamId}/invites/`, payload).then((r) => r.data)
  },

  cancelInvite(teamId: number | string, inviteId: number): Promise<void> {
    return api.post(`/api/teams/${teamId}/invites/${inviteId}/cancel/`).then((r) => r.data)
  },

  myInvites(): Promise<MyInvite[]> {
    return api.get('/api/teams/me/invites/').then((r) => r.data)
  },

  staffInbox(): Promise<StaffInboxResponse> {
    return api.get('/api/teams/me/staff-inbox/').then((r) => r.data)
  },

  markStaffInboxRead(): Promise<void> {
    return api.post('/api/teams/me/staff-inbox/mark-read/').then(() => undefined)
  },

  acceptInvite(inviteId: number): Promise<MyInvite> {
    return api.post(`/api/teams/me/invites/${inviteId}/accept/`).then((r) => r.data)
  },

  declineInvite(inviteId: number): Promise<MyInvite> {
    return api.post(`/api/teams/me/invites/${inviteId}/decline/`).then((r) => r.data)
  },

  getActivation(teamId: number | string): Promise<TeamActivation> {
    return api.get(`/api/teams/${teamId}/activation/`).then((r) => r.data)
  },

  setKioskPin(teamId: number | string, newPin: string): Promise<{ ok: true }> {
    return api.post(`/api/teams/${teamId}/activation/pin/`, { new_pin: newPin }).then((r) => r.data)
  },

  regenerateKioskSlug(teamId: number | string): Promise<TeamActivation> {
    return api.post(`/api/teams/${teamId}/activation/regenerate-slug/`).then((r) => r.data)
  },

  teamQrPngUrl(teamId: number | string): string {
    return `/api/teams/${teamId}/qr/`
  },

  downloadTeamQrA4Pdf(teamId: number | string, sides: TeamQrSides, pages = 1): Promise<Blob> {
    return api
      .get(`/api/teams/${teamId}/qr/print/a4/`, {
        params: { ...sides, pages },
        responseType: 'blob',
      })
      .then((r) => r.data)
  },

  downloadTeamQrSquarePdf(teamId: number | string, sides: TeamQrSides): Promise<Blob> {
    return api
      .get(`/api/teams/${teamId}/qr/print/square/`, {
        params: sides,
        responseType: 'blob',
      })
      .then((r) => r.data)
  },

  downloadPersonalQrA4Pdf(teamId: number | string, sides: TeamQrSides, pages = 1): Promise<Blob> {
    return api
      .get(`/api/teams/${teamId}/my-personal-tip-qr/print/a4/`, {
        params: { ...sides, pages },
        responseType: 'blob',
      })
      .then((r) => r.data)
  },

  downloadPersonalQrSquarePdf(teamId: number | string, sides: TeamQrSides): Promise<Blob> {
    return api
      .get(`/api/teams/${teamId}/my-personal-tip-qr/print/square/`, {
        params: sides,
        responseType: 'blob',
      })
      .then((r) => r.data)
  },

  downloadMemberQrA4Pdf(teamId: number | string, membershipId: number, sides: TeamQrSides, pages = 1): Promise<Blob> {
    return api
      .get(`/api/teams/${teamId}/members/${membershipId}/qr/print/a4/`, {
        params: { ...sides, pages },
        responseType: 'blob',
      })
      .then((r) => r.data)
  },

  downloadMemberQrSquarePdf(teamId: number | string, membershipId: number, sides: TeamQrSides): Promise<Blob> {
    return api
      .get(`/api/teams/${teamId}/members/${membershipId}/qr/print/square/`, {
        params: sides,
        responseType: 'blob',
      })
      .then((r) => r.data)
  },
}

// Kiosk — always use `kioskHttp` (no Bearer / refresh interceptor). Session is PIN-only via token.
export const kioskApi = {
  startSession(publicSlug: string, pin: string): Promise<KioskSessionResponse> {
    return kioskHttp.post(`/api/kiosk/activate/${publicSlug}/session/`, { pin }).then((r) => r.data)
  },

  members(sessionToken: string): Promise<KioskMember[]> {
    // Query param only — avoids non-simple CORS preflight vs custom header + JWT on shared axios.
    return kioskHttp.get('/api/kiosk/session/members/', { params: { token: sessionToken } }).then((r) => r.data)
  },

  toggle(sessionToken: string, membershipId: number, active: boolean): Promise<KioskMember> {
    return kioskHttp
      .post('/api/kiosk/session/toggle/', {
        membership_id: membershipId,
        active,
        session_token: sessionToken,
      })
      .then((r) => r.data)
  },

  identify(sessionToken: string, membershipId: number): Promise<{ ok: true }> {
    return kioskHttp
      .post('/api/kiosk/session/identify/', {
        membership_id: membershipId,
        session_token: sessionToken,
      })
      .then((r) => r.data)
  },
}
